import { compact, differenceBy, maxBy, orderBy, sum, uniqBy } from "lodash";
import moment from "moment";

import { assertNonNull } from "@helpers/helpers";

type EventType = { id: number; startDatetime: string; endDatetime: string };

export const eventsHaveOverlap = (event1: EventType, event2: EventType) => {
  return (
    moment(event2.startDatetime).isBefore(event1.endDatetime) &&
    moment(event2.endDatetime).isAfter(event1.startDatetime)
  );
};

export const getOverlappingEvents = (events: EventType[], event: EventType) => {
  return events.filter((e) => eventsHaveOverlap(e, event));
};

export const recurseAllOverlappingEventsOfOverlappingEvents = (
  events: EventType[],
  event: EventType,
  initialMemo: EventType[] = [],
): EventType[] => {
  return getOverlappingEvents(events, event).reduce(
    (memo, overlappingEvent) => {
      const matchingEvent = memo.find(({ id }) => overlappingEvent.id === id);
      if (matchingEvent) {
        return memo;
      }
      const newMemo = [...memo, overlappingEvent];
      return newMemo.concat(
        recurseAllOverlappingEventsOfOverlappingEvents(
          events,
          overlappingEvent,
          newMemo,
        ),
      );
    },
    initialMemo,
  );
};

export const getGroupedOverlappingEvents = (
  overlappingEvents: EventType[],
  index = 0,
  parent: any = null,
): any => {
  // events on same level and not overlapping each other
  const eventsOnSameLevel = overlappingEvents.reduce(
    (memo: { keep: EventType[]; ignore: EventType[] }, overlap: EventType) => {
      // return memo if event overlap with previous one
      if (
        memo.keep.length > 0 &&
        eventsHaveOverlap(memo.keep[memo.keep.length - 1], overlap)
      ) {
        return memo;
      }

      // ignore event if event on same level do not overlap with parent
      if (parent && !eventsHaveOverlap(parent, overlap)) {
        return {
          ...memo,
          ignore: [...memo.ignore, overlap],
        };
      }

      // add event to memo
      const overlapToAdd = {
        ...overlap,
        children: [],
        index: index + 1,
        path: compact((parent?.path || []).concat(overlap.id)),
        parent,
      };
      return {
        ...memo,
        keep: memo.keep.concat(overlapToAdd),
      };
    },
    { keep: [], ignore: [] },
  );
  const eventsOnSameLevelAndNotOverlappingEachOther = eventsOnSameLevel.keep;
  const eventsToIgnore = eventsOnSameLevel.ignore;

  // if no data we stopped execution of recurring loop
  if (eventsOnSameLevelAndNotOverlappingEachOther.length === 0) {
    return eventsOnSameLevelAndNotOverlappingEachOther;
  }
  const newOverlappingEvents = differenceBy(
    overlappingEvents,
    eventsOnSameLevelAndNotOverlappingEachOther.concat(eventsToIgnore),
    "id",
  );
  if (newOverlappingEvents.length === 0) {
    return eventsOnSameLevelAndNotOverlappingEachOther;
  }

  return eventsOnSameLevelAndNotOverlappingEachOther.map((overlap) => ({
    ...overlap,
    children: getGroupedOverlappingEvents(
      newOverlappingEvents,
      index + 1,
      overlap,
    ),
  }));
};

const getLongestPathInGroupedOverlaps = (groupedOverlaps: any) => {
  return groupedOverlaps.reduce((_memo: number, overlap: any) => {
    return Math.max(
      overlap.path.length,
      getLongestPathInGroupedOverlaps(overlap.children),
    );
  }, 0);
};

export class OverlappingEvents {
  events: EventType[];

  cache = {
    getMaxOverlapCountOfEvent: {} as { [key: string]: any },
    getMaxOverlapCountOfEventsOverlappingEvent: {} as { [key: string]: any },
    getAllOverlappingEventsOfOverlappingEvents: {} as { [key: string]: any },
    getLongestPathOfEventInGroupedOverlappingEvents: {} as {
      [key: string]: any;
    },
    getEventPositioning: {} as { [key: string]: any },
  };

  constructor(events: EventType[]) {
    this.events = events;
  }

  getMaxOverlapCountOfEvent(event: EventType) {
    if (this.cache.getMaxOverlapCountOfEvent[event.id]) {
      return this.cache.getMaxOverlapCountOfEvent[event.id];
    }
    const overlappingEvents = getOverlappingEvents(this.events, event);
    const groupedOverlaps = getGroupedOverlappingEvents(
      overlappingEvents,
      0,
      null,
    );
    this.cache.getMaxOverlapCountOfEvent[event.id] =
      getLongestPathInGroupedOverlaps(groupedOverlaps);
    return this.cache.getMaxOverlapCountOfEvent[event.id];
  }

  getMaxOverlapCountOfEventsOverlappingEvent(event: EventType) {
    if (this.cache.getMaxOverlapCountOfEventsOverlappingEvent[event.id]) {
      return this.cache.getMaxOverlapCountOfEventsOverlappingEvent[event.id];
    }
    const allOverlappingEvents =
      this.getAllOverlappingEventsOfOverlappingEvents(event);
    const results = uniqBy(allOverlappingEvents, "id").map((e) =>
      this.getMaxOverlapCountOfEvent(e),
    );
    this.cache.getMaxOverlapCountOfEventsOverlappingEvent[event.id] = Math.max(
      ...results,
    );
    return this.cache.getMaxOverlapCountOfEventsOverlappingEvent[event.id];
  }

  findLongestPathOfEventInGroupedOverlappingEvents(
    groupedOverlaps: any,
    event: EventType,
  ) {
    const paths = groupedOverlaps.reduce((memo: any, overlap: any) => {
      const childrenPath =
        this.findLongestPathOfEventInGroupedOverlappingEvents(
          overlap.children,
          event,
        ) || [];
      const validPaths = compact([overlap.path, memo, childrenPath]).filter(
        (path) => path.includes(event.id),
      );
      return maxBy(validPaths, (path) => path.length);
    }, []);
    return paths;
  }

  getAllOverlappingEventsOfOverlappingEvents(event: EventType): EventType[] {
    if (this.cache.getAllOverlappingEventsOfOverlappingEvents[event.id]) {
      return this.cache.getAllOverlappingEventsOfOverlappingEvents[event.id];
    }
    this.cache.getAllOverlappingEventsOfOverlappingEvents[event.id] = uniqBy(
      recurseAllOverlappingEventsOfOverlappingEvents(this.events, event),
      "id",
    );
    return this.cache.getAllOverlappingEventsOfOverlappingEvents[event.id];
  }

  getLongestPathOfEventInGroupedOverlappingEvents(event: EventType) {
    // return cached data
    if (this.cache.getLongestPathOfEventInGroupedOverlappingEvents[event.id]) {
      return this.cache.getLongestPathOfEventInGroupedOverlappingEvents[
        event.id
      ];
    }

    const allOverlappingEvents =
      this.getAllOverlappingEventsOfOverlappingEvents(event);
    const sortedOverlappingEvents = orderBy(
      allOverlappingEvents,
      ["startDatetime", "duration"],
      ["asc", "desc"],
    );
    const groupedOverlaps = getGroupedOverlappingEvents(
      sortedOverlappingEvents,
      0,
      null,
    );

    this.cache.getLongestPathOfEventInGroupedOverlappingEvents[event.id] =
      this.findLongestPathOfEventInGroupedOverlappingEvents(
        groupedOverlaps,
        event,
      );
    return this.cache.getLongestPathOfEventInGroupedOverlappingEvents[event.id];
  }

  getEventPositioning(event: EventType) {
    // return cached data
    if (this.cache.getEventPositioning[event.id]) {
      return this.cache.getEventPositioning[event.id];
    }

    const maxCount = this.getMaxOverlapCountOfEventsOverlappingEvent(event);
    const longestPathWithEvent =
      this.getLongestPathOfEventInGroupedOverlappingEvents(event);
    const index = longestPathWithEvent.findIndex(
      (id: number) => id === event.id,
    );
    const pathsOfEventInLongestPath = longestPathWithEvent.map(
      (pathId: number) => {
        const matchingEvent = assertNonNull(
          this.events.find((e) => e.id === pathId),
        );
        return this.getLongestPathOfEventInGroupedOverlappingEvents(
          matchingEvent,
        );
      },
    );

    const widths = pathsOfEventInLongestPath
      .reduce((memo: any, path: any, i: number) => {
        const memoAdd = sum(memo);
        const width = maxCount - memoAdd - (path.length - i) + 1;
        return memo.concat(width);
      }, [])
      .map((width: number) => (width * 100) / maxCount);

    this.cache.getEventPositioning[event.id] = {
      left: sum(widths.slice(0, index)),
      width: widths[index],
    };
    return this.cache.getEventPositioning[event.id];
  }

  getAugmentedEvents() {
    const sortedEvents = orderBy(
      this.events,
      ["startDatetime", "duration"],
      ["asc", "desc"],
    );
    return sortedEvents.map((event) => {
      return {
        ...event,
        ...this.getEventPositioning(event),
      };
    });
  }
}
